import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { AXIS_LOGO_DATA_URI } from './shared/axisLogo';
import {
  FileSearch, ShieldCheck, Settings, LogOut, ChevronsLeft, ChevronsRight,
  ChevronRight, Sun, Moon, FileCog,
} from 'lucide-react';
import { ReconJobListPage } from './modules/module-1-portfolio-dp-recon/pages/JobListPage';
import { HoldingReconDetailPage } from './modules/holding-recon/pages/HoldingReconDetailPage';
import { FnoReconListPage } from './modules/fno-recon/pages/FnoReconListPage';
import { NewFnoReconPage } from './modules/fno-recon/pages/NewFnoReconPage';
import { FnoReconDetailPage } from './modules/fno-recon/pages/FnoReconDetailPage';
import { ReconJobDetailPage } from './modules/module-1-portfolio-dp-recon/pages/JobDetailPage';
import { VerifyExtractionPage } from './modules/module-1-portfolio-dp-recon/verify/VerifyExtractionPage';
import { NewReconJobPage } from './modules/module-1-portfolio-dp-recon/pages/NewJobPage';
import { DocValidationJobListPage } from './modules/module-2-document-validation/pages/JobListPage';
import { DocValidationJobDetailPage } from './modules/module-2-document-validation/pages/JobDetailPage';
import { NewDocValidationJobPage } from './modules/module-2-document-validation/pages/NewJobPage';
import { AuditLogPage } from './modules/admin/pages/AuditLogPage';
import { CorporateActionsPage } from './modules/admin/pages/CorporateActionsPage';
import { UsersPage } from './modules/admin/pages/UsersPage';
import { PromptsPage } from './modules/admin/pages/PromptsPage';
import { DiagnosticsPage } from './modules/admin/pages/DiagnosticsPage';
import { ReconLogicPage } from './modules/admin/pages/ReconLogicPage';
import { AdminLayout } from './modules/admin/AdminLayout';
import { PfFileGenPage } from './modules/pf-file-gen/PfFileGenPage';
import { DpFileGenPage } from './modules/dp-file-gen/DpFileGenPage';
import { PfFileGenNewPage } from './modules/pf-file-gen-new/PfFileGenNewPage';
import { LoginPage } from './modules/auth/pages/LoginPage';
import { useAuth } from './shared/auth';
import { useTheme } from './shared/theme';

const ROLE_BADGE_STYLES: Record<string, string> = {
  super_admin: 'pill-accent',
  admin:       'pill-accent',
  checker:     'pill-ok',
  maker:       'pill-info',
  viewer:      'pill-neutral',
};

interface NavItem {
  to: string;
  icon: ReactNode;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/recon',           icon: <FileSearch  className="w-5 h-5" />, label: 'Recon' },
  { to: '/fno-recon',       icon: <FileSearch  className="w-5 h-5" />, label: 'FNO Recon' },
  { to: '/bo-cmots-file',   icon: <FileCog     className="w-5 h-5" />, label: 'BO+CMOTS File' },
  { to: '/dp-file-gen',     icon: <FileCog     className="w-5 h-5" />, label: 'DP File Gen' },
  { to: '/pf-file-gen',     icon: <FileCog     className="w-5 h-5" />, label: 'PF File Gen' },
  { to: '/docs',            icon: <ShieldCheck className="w-5 h-5" />, label: 'Documents' },
  { to: '/admin',           icon: <Settings    className="w-5 h-5" />, label: 'Admin' },
];

export function App() {
  const { user, isLoading, authReady, hasCredentials, signOut } = useAuth();
  // Sidebar: collapsed by default per spec; persist user preference.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem('ab_sidebar_collapsed');
    if (stored === null) return true; // default collapsed
    return stored === '1';
  });
  useEffect(() => {
    localStorage.setItem('ab_sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // Splash while MSAL probes /api/me
  if (!authReady) {
    return <Splash label="Initializing" />;
  }
  if (!hasCredentials) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }
  if (isLoading) {
    return <Splash label="Loading workspace" />;
  }

  return (
    <div className="relative min-h-screen flex">
      {/* Aurora atmosphere — sticks behind everything */}
      <div
        className="fixed inset-0 pointer-events-none -z-10"
        style={{
          backgroundImage:
            'radial-gradient(ellipse 70% 50% at 15% 0%, rgba(255,31,107,0.12), transparent 60%),' +
            'radial-gradient(ellipse 60% 50% at 90% 50%, rgba(77,163,255,0.08), transparent 60%)',
        }}
      />

      {/* Collapsible sidebar */}
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
        user={user}
        onSignOut={signOut}
      />

      {/* Main content — leaves left padding for the sidebar */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'ml-16' : 'ml-60'}`}>
        <Routes>
          <Route path="/" element={<Navigate to="/recon" replace />} />
          <Route path="/recon" element={<ReconJobListPage />} />
          <Route path="/recon/new" element={<NewReconJobPage />} />
          <Route path="/recon/:id" element={<ReconJobDetailPage />} />
          <Route path="/recon/:id/verify" element={<VerifyExtractionPage />} />
          <Route path="/holding-recon/:id" element={<HoldingReconDetailPage />} />
          <Route path="/fno-recon" element={<FnoReconListPage />} />
          <Route path="/fno-recon/new" element={<NewFnoReconPage />} />
          <Route path="/fno-recon/:id" element={<FnoReconDetailPage />} />
          <Route path="/bo-cmots-file" element={<PfFileGenPage />} />
          <Route path="/dp-file-gen" element={<DpFileGenPage />} />
          <Route path="/pf-file-gen" element={<PfFileGenNewPage />} />
          <Route path="/docs" element={<DocValidationJobListPage />} />
          <Route path="/docs/new" element={<NewDocValidationJobPage />} />
          <Route path="/docs/:id" element={<DocValidationJobDetailPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/corporate-actions" replace />} />
            <Route path="corporate-actions" element={<CorporateActionsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="prompts" element={<PromptsPage />} />
            <Route path="recon-logic" element={<ReconLogicPage />} />
            <Route path="diagnostics" element={<DiagnosticsPage />} />
            <Route path="audit" element={<AuditLogPage />} />
          </Route>
          <Route path="/login" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/** ─── Sidebar ─────────────────────────────────────────────────────────── */

function Sidebar({
  collapsed,
  onToggle,
  user,
  onSignOut,
}: {
  collapsed: boolean;
  onToggle: () => void;
  user: { actor_email: string; actor_role: string } | null;
  onSignOut: () => void;
}) {
  const loc = useLocation();
  const { theme, toggleTheme } = useTheme();
  return (
    <aside
      className={`sidebar-chrome fixed top-0 left-0 h-screen z-30 transition-all duration-300 ease-out
        ${collapsed ? 'w-16' : 'w-60'}`}
      style={{
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--brand-darker)',
      }}
    >
      <div className="flex flex-col h-full">
        {/* Brand */}
        <div className={`flex items-center h-16 ${collapsed ? 'justify-center' : 'justify-between px-4'}`}>
          <div className="flex items-center gap-2.5">
            <SidebarMark />
            {!collapsed && (
              <div className="leading-none">
                <div className="text-sm font-semibold tracking-tight text-ink-strong">Agentic</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400 mt-0.5">Backoffice</div>
              </div>
            )}
          </div>
          {!collapsed && (
            <button
              onClick={onToggle}
              title="Collapse sidebar"
              className="w-7 h-7 rounded-md flex items-center justify-center text-ink-400 hover:text-ink-strong transition"
              style={{ background: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Expand toggle (only visible when collapsed) */}
        {collapsed && (
          <button
            onClick={onToggle}
            title="Expand sidebar"
            className="mx-2 mb-3 h-8 rounded-md flex items-center justify-center text-ink-400 hover:text-ink-strong transition"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        )}

        {/* Nav items */}
        <nav className="flex-1 flex flex-col gap-1 px-2">
          {NAV_ITEMS.map(item => (
            <SidebarLink
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              collapsed={collapsed}
              active={loc.pathname === item.to || loc.pathname.startsWith(item.to + '/')}
            />
          ))}
        </nav>

        {/* Theme toggle */}
        <div className={`px-2 ${collapsed ? '' : 'px-3'}`}>
          <button
            type="button"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            className={`group relative w-full flex items-center gap-3 rounded-xl transition text-ink-400 hover:text-ink-strong
              ${collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2'}`}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {!collapsed && (
              <span className="text-xs tracking-tight">
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </span>
            )}
            {collapsed && (
              <span
                className="pointer-events-none absolute left-full ml-3 px-2 py-1 rounded-md text-xs whitespace-nowrap
                           opacity-0 group-hover:opacity-100 transition delay-150"
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--text-strong)',
                }}
              >
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </span>
            )}
          </button>
        </div>

        {/* User block at bottom */}
        <div className={`mt-2 p-2 ${collapsed ? '' : 'p-3'}`} style={{ borderTop: '1px solid var(--glass-border)' }}>
          {user && (
            <div className={`${collapsed ? 'flex flex-col items-center gap-2' : 'space-y-2'}`}>
              {!collapsed && (
                <div className="px-2 py-1.5 rounded-lg" style={{ background: 'var(--bg-hover)' }}>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-400">Signed in</div>
                  <div className="text-xs text-ink-strong truncate font-medium mt-0.5">{user.actor_email}</div>
                  <div className={`mt-1.5 pill ${ROLE_BADGE_STYLES[user.actor_role] ?? ROLE_BADGE_STYLES.viewer}`}>
                    {user.actor_role}
                  </div>
                </div>
              )}
              {collapsed && (
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, var(--accent-glow) 0%, var(--accent) 100%)',
                    color: '#fff',
                    boxShadow: '0 4px 12px 0 var(--accent-soft)',
                  }}
                  title={`${user.actor_email} (${user.actor_role})`}
                >
                  {initials(user.actor_email)}
                </div>
              )}
              <button
                type="button"
                onClick={onSignOut}
                title="Sign out"
                className={`${collapsed ? 'w-9 h-9' : 'w-full'} inline-flex items-center justify-center gap-2 rounded-lg text-xs text-ink-400 hover:text-ink-strong transition py-2`}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <LogOut className="w-4 h-4" />
                {!collapsed && <span>Sign out</span>}
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function SidebarLink({
  to, icon, label, collapsed, active,
}: {
  to: string; icon: ReactNode; label: string; collapsed: boolean; active: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={`group relative flex items-center gap-3 rounded-xl transition
        ${collapsed ? 'justify-center px-0 py-3 mx-1' : 'px-3 py-2.5'}
        ${active ? 'text-ink-strong' : 'text-ink-400 hover:text-ink-strong'}`}
      style={active ? {
        background: 'linear-gradient(135deg, var(--accent-soft) 0%, transparent 100%)',
        border: '1px solid var(--accent)',
        boxShadow: 'inset 0 1px 0 0 var(--glass-edge), 0 4px 16px 0 var(--accent-soft)',
      } : undefined}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ color: active ? 'var(--accent)' : undefined }}>{icon}</span>
      {!collapsed && <span className="text-sm font-medium tracking-tight">{label}</span>}
      {!collapsed && active && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-50" />}
      {collapsed && (
        <span
          className="pointer-events-none absolute left-full ml-3 px-2 py-1 rounded-md text-xs whitespace-nowrap
                     opacity-0 group-hover:opacity-100 transition delay-150"
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--glass-border)',
            color: 'var(--text-strong)',
          }}
        >
          {label}
        </span>
      )}
    </NavLink>
  );
}

/** ─── Splash / shared ───────────────────────────────────────────────── */

function Splash({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex items-center gap-3 text-ink-200">
        <div className="w-5 h-5 rounded-full border-2 border-accent/30 border-t-accent animate-spin" />
        <span className="text-sm tracking-tight">{label}…</span>
      </div>
    </div>
  );
}

function SidebarMark() {
  // Axis "A" logo, rendered at the same 30x30 size as the previous mark.
  return (
    <img
      src={AXIS_LOGO_DATA_URI}
      width={30}
      height={30}
      alt="Axis"
      style={{ width: 30, height: 30, objectFit: 'contain', display: 'block' }}
    />
  );
}

function initials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}
